import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  CheckSquareOffset, CheckSquare, Square, Sparkle, ArrowClockwise,
  Copy, NotePencil, CalendarCheck, Target,
} from "@phosphor-icons/react";
import { PageHeader } from "../components/ui/kaizen";

const GOLD = "#D4A24C";
const RED = "#D90429";

const FORMAT_COLORS = {
  Reel: "#D90429",
  Carrousel: "#7c3aed",
  Story: "#db2777",
  Photo: "#0891b2",
  Texte: "#16a34a",
};

export default function MarketingPlan30Module() {
  const [data, setData] = useState(null);
  const [openDay, setOpenDay] = useState(null);
  const [filter, setFilter] = useState("all"); // all | todo | done

  const load = async () => {
    try {
      const { data } = await api.get("/marketing/plan30");
      setData(data);
    } catch { toast.error("Erreur de chargement du plan"); }
  };

  useEffect(() => { load(); }, []);

  const toggleDone = async (day, done) => {
    try {
      await api.patch(`/marketing/plan30/${day}`, { done });
      load();
    } catch { toast.error("Erreur"); }
  };

  const resetPlan = async () => {
    if (!window.confirm("Réinitialiser tout le plan (cases cochées + notes + légendes) ?")) return;
    try {
      await api.post("/marketing/plan30/reset");
      toast.success("Plan réinitialisé");
      load();
    } catch { toast.error("Erreur"); }
  };

  const days = (data?.days || []).filter((d) =>
    filter === "all" ? true : filter === "todo" ? !d.done : d.done
  );

  // Regrouper par semaine
  const byWeek = {};
  days.forEach((d) => { (byWeek[d.week] ||= []).push(d); });

  return (
    <div data-testid="marketing-plan30-page" className="min-h-screen">
      {/* HEADER */}
      <div className="px-8 lg:px-12 pt-8">
        <PageHeader
          title="Checklist Marketing 30 Jours"
          breadcrumbs={[{ label: "Marketing & contenu" }, { label: "Plan 30 jours" }]}
          subtitle="Un plan éditorial concret ancré sur STEPPS et l'approche Inbound. Une action par jour pour la reconquête estivale — un degré à la fois."
          actions={
            <button data-testid="btn-reset-plan" onClick={resetPlan} className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-zinc-700 hover:border-zinc-500 text-zinc-200 text-sm transition-colors">
              <ArrowClockwise size={16} /> Réinitialiser
            </button>
          }
        />
        {data && (
          <div className="flex items-center gap-4 flex-wrap mb-2">
            <div className="flex-1 min-w-[220px] max-w-md">
              <div className="flex justify-between text-xs uppercase tracking-wide text-zinc-400 mb-1">
                <span>{data.done} / {data.total} actions complétées</span>
                <span style={{ color: GOLD }}>{data.progress_pct}%</span>
              </div>
              <div className="h-2.5 bg-zinc-800 rounded-full overflow-hidden">
                <div data-testid="plan30-progress" className="h-full transition-all duration-700 rounded-full" style={{ width: `${data.progress_pct}%`, background: GOLD }} />
              </div>
            </div>
            <div className="flex gap-2">
              {["all", "todo", "done"].map((f) => (
                <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
                  className="text-xs uppercase tracking-wide px-3 py-1.5 rounded-lg border transition-colors"
                  style={f === filter ? { background: GOLD, color: "#18181b", borderColor: GOLD } : { borderColor: "#3f3f46", color: "#a1a1aa" }}>
                  {f === "all" ? "Tout" : f === "todo" ? "À faire" : "Faits"}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="px-8 lg:px-12 py-8 space-y-10">
        {/* Piliers */}
        {data && (
          <div className="flex flex-wrap gap-2" data-testid="plan30-pillars">
            {data.pillars.map((p) => (
              <span key={p.key} className="ticker px-3 py-1.5 hard-border bg-secondary/40 flex items-center gap-1.5">
                <span>{p.emoji}</span> {p.label} · {p.count}
              </span>
            ))}
          </div>
        )}

        {Object.keys(byWeek).sort((a, b) => a - b).map((wk) => (
          <section key={wk}>
            <div className="flex items-center gap-2 mb-4">
              <CalendarCheck size={20} weight="duotone" style={{ color: RED }} />
              <h2 className="font-display font-bold text-xl">{byWeek[wk][0].week_label}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {byWeek[wk].map((d) => (
                <DayCard key={d.day} d={d} onToggle={toggleDone} onOpen={() => setOpenDay(d.day)} />
              ))}
            </div>
          </section>
        ))}

        {days.length === 0 && (
          <div className="hard-border p-8 text-center text-sm text-muted-foreground bg-secondary/20">
            Aucune action dans ce filtre.
          </div>
        )}
      </div>

      {openDay && (
        <DayModal day={openDay} onClose={() => setOpenDay(null)} onSaved={load} />
      )}
    </div>
  );
}

function DayCard({ d, onToggle, onOpen }) {
  const fc = FORMAT_COLORS[d.format] || "#64748b";
  return (
    <div className="hard-border bg-white p-4 flex flex-col" data-testid={`day-card-${d.day}`} style={d.done ? { opacity: 0.6 } : {}}>
      <div className="flex items-start gap-3">
        <button data-testid={`toggle-day-${d.day}`} onClick={() => onToggle(d.day, !d.done)} className="mt-0.5 flex-shrink-0">
          {d.done ? <CheckSquare size={24} weight="fill" style={{ color: "#16a34a" }} /> : <Square size={24} style={{ color: "#9ca3af" }} />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="ticker font-bold" style={{ color: RED }}>JOUR {d.day}</span>
            <span className="ticker px-2 py-0.5 text-white" style={{ background: fc, fontSize: "10px" }}>{d.format}</span>
          </div>
          <div className="font-display font-bold text-base mt-1 leading-tight">{d.title}</div>
          <div className="ticker text-muted-foreground mt-1">{d.pillar_emoji} {d.pillar_label} · STEPPS : {d.stepps}</div>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mt-3 line-clamp-3 flex-1">{d.idea}</p>
      <div className="mt-3 text-sm font-medium" style={{ color: "#18181b" }}>👉 {d.cta}</div>
      <div className="flex items-center justify-between mt-3 pt-3 hard-border-t">
        <div className="flex gap-1.5 flex-wrap">
          {d.channels.map((c) => (
            <span key={c} className="ticker text-muted-foreground" style={{ fontSize: "10px" }}>{c}</span>
          ))}
        </div>
        <button data-testid={`open-day-${d.day}`} onClick={onOpen} className="flex items-center gap-1.5 ticker hover:text-[#D90429] transition-colors">
          <Sparkle size={14} /> Légende IA / Notes
        </button>
      </div>
    </div>
  );
}

function DayModal({ day, onClose, onSaved }) {
  const [item, setItem] = useState(null);
  const [notes, setNotes] = useState("");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/marketing/plan30");
      const d = data.days.find((x) => x.day === day);
      setItem(d);
      setNotes(d?.notes || "");
      setCaption(d?.caption || "");
    })();
  }, [day]);

  const saveNotes = async () => {
    try {
      await api.patch(`/marketing/plan30/${day}`, { notes });
      toast.success("Notes enregistrées");
      onSaved();
    } catch { toast.error("Erreur"); }
  };

  const genCaption = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/marketing/plan30/${day}/caption`);
      setCaption(data.caption);
      toast.success("Légende générée");
      onSaved();
    } catch { toast.error("Génération IA indisponible"); }
    finally { setBusy(false); }
  };

  const copyCaption = () => {
    navigator.clipboard.writeText(caption);
    toast.success("Légende copiée");
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-background hard-border max-w-2xl w-full max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="day-modal">
        <div className="hard-border-b px-5 py-4 sticky top-0 bg-background">
          <div className="ticker text-muted-foreground">Jour {day} {item ? `· ${item.format}` : ""}</div>
          <div className="font-display font-bold text-lg">{item?.title || "…"}</div>
        </div>
        {item && (
          <div className="p-5 space-y-5">
            <div className="hard-border p-4 bg-secondary/30 text-sm">
              <div className="ticker text-muted-foreground mb-1">{item.pillar_emoji} {item.pillar_label} · STEPPS : {item.stepps}</div>
              {item.idea}
              <div className="mt-2 font-medium">👉 {item.cta}</div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="ticker text-muted-foreground flex items-center gap-1.5"><Sparkle size={14} /> Légende prête-à-publier (IA)</label>
                <button data-testid="gen-caption-btn" onClick={genCaption} disabled={busy}
                  className="ticker px-3 py-1.5 text-white disabled:opacity-50" style={{ background: RED }}>
                  {busy ? "Génération…" : caption ? "Régénérer" : "Générer"}
                </button>
              </div>
              {caption ? (
                <div className="hard-border p-4 bg-white">
                  <div className="text-sm whitespace-pre-wrap leading-relaxed" data-testid="caption-text">{caption}</div>
                  <button data-testid="copy-caption-btn" onClick={copyCaption} className="mt-3 flex items-center gap-1.5 ticker hover:text-[#D90429]">
                    <Copy size={14} /> Copier
                  </button>
                </div>
              ) : (
                <div className="hard-border p-4 text-sm text-muted-foreground bg-secondary/20">
                  Clique « Générer » pour obtenir une légende en français ancrée sur le principe STEPPS du jour.
                </div>
              )}
            </div>

            <div>
              <label className="ticker text-muted-foreground flex items-center gap-1.5 mb-2"><NotePencil size={14} /> Mes notes</label>
              <textarea data-testid="day-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                placeholder="Idées, lien du visuel, date prévue…" className="w-full hard-border px-4 py-2.5 text-sm" />
            </div>
          </div>
        )}
        <div className="hard-border-t p-5 flex justify-between sticky bottom-0 bg-background">
          <button onClick={onClose} className="ticker hover:text-[#D90429]">Fermer</button>
          <button data-testid="save-notes-btn" onClick={saveNotes} className="px-6 py-2.5 ticker text-zinc-950" style={{ background: GOLD }}>Enregistrer les notes</button>
        </div>
      </div>
    </div>
  );
}
